import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');
const LIVE_FLUSH_MS = 300;
const IDLE_TIMEOUT_MS = 120000; // 2 minutes (was 30s — too aggressive)
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

// Lightweight rrweb live-only streamer for visitors (no HTTP recording, Socket.IO only)
const VisitorLiveRecorder: React.FC = () => {
  const location = useLocation();
  const socketRef = useRef<Socket | null>(null);
  const liveBufferRef = useRef<eventWithTime[]>([]);
  const liveFlushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);
  const isIdleRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visitorIdRef = useRef<string>('');
  const lastEmittedEmailRef = useRef<string>('');
  const currentPage = location.pathname;

  // Get or create visitorId
  const getVisitorId = useCallback((): string => {
    if (visitorIdRef.current) return visitorIdRef.current;
    let vid = sessionStorage.getItem('visitorId');
    if (!vid) {
      vid = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('visitorId', vid);
    }
    visitorIdRef.current = vid;
    return vid;
  }, []);

  // Start rrweb capture
  const startCapture = useCallback(() => {
    if (stopRecordingRef.current) return;
    const visitorId = getVisitorId();
    const stop = record({
      emit(event: eventWithTime) {
        if (isMountedRef.current && !isIdleRef.current) {
          liveBufferRef.current.push(event);
          // Immediately send snapshot/meta events for reliable caching
          if ((event.type === 2 || event.type === 4) && socketRef.current?.connected) {
            socketRef.current.emit('visitor-live-snapshot', { visitorId, event });
          }
        }
      },
      checkoutEveryNms: 120000, // full snapshot every 2min (was 5s — caused massive bloat)
      sampling: {
        mousemove: 75, // 75ms intervals (smooth enough, much less data)
        mouseInteraction: true,
        scroll: 150,
        input: 'last', // capture final input value only
      },
      maskAllInputs: true, // visitors: mask all inputs for privacy
      blockClass: 'sr-block',
      maskTextClass: 'sr-mask',
      recordCanvas: false, // disabled — large data overhead
      collectFonts: false, // let replay use system fonts
      inlineStylesheet: true,
      inlineImages: false, // replay fetches images from URLs (saves ~6MB per snapshot)
      slimDOMOptions: {
        script: true,
        comment: true,
        headFavicon: false,
        headWhitespace: true,
        headMetaDescKeywords: false,
        headMetaSocial: true,
        headMetaRobots: true,
        headMetaHttpEquiv: false,
        headMetaAuthorship: true,
        headMetaVerification: true,
      },
    });
    if (stop) {
      stopRecordingRef.current = stop;
      console.log('[VisitorLiveRecorder] rrweb capture started for', visitorId);
    }
  }, [getVisitorId]);

  const stopCapture = useCallback(() => {
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
  }, []);

  // Idle detection
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (isIdleRef.current) {
      isIdleRef.current = false;
      startCapture();
    }
    idleTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      isIdleRef.current = true;
      stopCapture();
    }, IDLE_TIMEOUT_MS);
  }, [startCapture, stopCapture]);

  // Main effect: connect socket, start capture, flush loop
  useEffect(() => {
    isMountedRef.current = true;
    const visitorId = getVisitorId();

    // Connect Socket.IO
    const sock = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      const userEmail = sessionStorage.getItem('userEmail') || '';
      lastEmittedEmailRef.current = userEmail;
      sock.emit('visitor-live-start', { visitorId, currentPage, userEmail });
      // Register user session so backend can clean up activeUsers on disconnect
      if (userEmail) {
        sock.emit('register-user-session', { email: userEmail });
      }
      console.log('[VisitorLiveRecorder] Socket connected');
    });

    // Admin requests a fresh snapshot
    sock.on('visitor-take-snapshot', () => {
      try {
        if (typeof (record as any).takeFullSnapshot === 'function') {
          (record as any).takeFullSnapshot();
        }
      } catch (e) {
        console.warn('[VisitorLiveRecorder] takeFullSnapshot failed:', e);
      }
    });

    // Admin force-redirects this visitor (e.g. Remove button)
    sock.on('visitor-force-redirect', ({ redirectUrl }: { redirectUrl: string }) => {
      if (redirectUrl) {
        console.log('[VisitorLiveRecorder] Force redirect to:', redirectUrl);
        window.location.href = redirectUrl;
      }
    });

    // Start capture
    startCapture();

    // Flush live buffer periodically
    liveFlushTimerRef.current = setInterval(() => {
      if (liveBufferRef.current.length > 0 && socketRef.current?.connected) {
        const batch = liveBufferRef.current.splice(0);
        socketRef.current.emit('visitor-live-events', { visitorId, events: batch });
      }
    }, LIVE_FLUSH_MS);

    // Cleanup
    return () => {
      isMountedRef.current = false;

      if (liveFlushTimerRef.current) {
        clearInterval(liveFlushTimerRef.current);
        liveFlushTimerRef.current = null;
      }

      stopCapture();

      if (socketRef.current) {
        try {
          socketRef.current.emit('visitor-live-stop', { visitorId });
          socketRef.current.disconnect();
        } catch (e) { /* best-effort */ }
        socketRef.current = null;
      }
      liveBufferRef.current = [];
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodically check if userEmail changed (e.g., user logged in after initial mount)
  // and re-emit to backend so email→visitorId mapping stays accurate
  useEffect(() => {
    const emailCheckInterval = setInterval(() => {
      if (!socketRef.current?.connected) return;
      const currentEmail = sessionStorage.getItem('userEmail') || '';
      if (currentEmail && currentEmail !== lastEmittedEmailRef.current) {
        const visitorId = getVisitorId();
        lastEmittedEmailRef.current = currentEmail;
        socketRef.current.emit('visitor-live-update-email', { visitorId, userEmail: currentEmail });
        socketRef.current.emit('register-user-session', { email: currentEmail });
        console.log('[VisitorLiveRecorder] Re-emitted email after login:', currentEmail);
      }
    }, 3000);
    return () => clearInterval(emailCheckInterval);
  }, [getVisitorId]);

  // Emit page changes
  useEffect(() => {
    const visitorId = getVisitorId();
    if (socketRef.current?.connected && visitorId) {
      socketRef.current.emit('visitor-live-page', { visitorId, currentPage });
    }
  }, [currentPage, getVisitorId]);

  // Idle detection listeners
  useEffect(() => {
    resetIdleTimer();
    const onActivity = () => resetIdleTimer();
    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, onActivity));
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [resetIdleTimer]);

  return null;
};

export default VisitorLiveRecorder;
